import { FC, useMemo } from 'react'
import { useGameStore } from '@/store'

interface TradeHistoryPanelProps {
  maxItems?: number
}

export const TradeHistoryPanel: FC<TradeHistoryPanelProps> = ({ maxItems = 20 }) => {
  const { session } = useGameStore()

  const trades = useMemo(() => {
    if (!session?.tradeHistory) return []
    return [...session.tradeHistory]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, maxItems)
  }, [session?.tradeHistory, maxItems])

  if (trades.length === 0) {
    return (
      <div className="p-4 text-center">
        <p className="font-retro text-sm text-game-muted">No trades yet</p>
        <p className="font-retro text-xs text-game-muted/60 mt-1">
          Your trading history will appear here
        </p>
      </div>
    )
  }

  const formatTime = (timestamp: Date | string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const formatAmount = (amount: number, symbol: string) => {
    if (symbol === 'USDC') return `$${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    if (amount < 1) return amount.toFixed(6)
    if (amount < 100) return amount.toFixed(4)
    return amount.toLocaleString(undefined, { maximumFractionDigits: 2 })
  }

  // Count total volume
  const totalVolume = useMemo(() => {
    return trades.reduce((sum, t) => {
      // Estimate USD value: if selling to USDC, use toAmount; if buying from USDC, use fromAmount
      if (t.toToken === 'USDC') return sum + t.toAmount
      if (t.fromToken === 'USDC') return sum + t.fromAmount
      // For swaps, estimate based on the price
      return sum + (t.toAmount * t.price)
    }, 0)
  }, [trades])

  return (
    <div className="p-2 space-y-2">
      {/* Summary Header */}
      <div className="flex justify-between items-center pb-2 border-b border-game-border">
        <span className="font-pixel text-xs text-game-muted">TRADE HISTORY</span>
        <div className="flex items-center gap-2">
          <span className="font-retro text-xs text-game-muted">Volume:</span>
          <span className="font-mono text-sm font-bold text-game-accent">
            ${totalVolume.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        </div>
      </div>

      {/* Trade List */}
      <div className="space-y-1 max-h-[300px] overflow-y-auto">
        {trades.map((trade, index) => {
          const isBuy = trade.type === 'buy'

          return (
            <div
              key={`${trade.timestamp}-${index}`}
              className="p-2 bg-game-bg border border-game-border/50 hover:border-game-border transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className={`font-pixel text-xs px-1.5 py-0.5 ${
                    isBuy
                      ? 'bg-game-profit/20 text-game-profit border border-game-profit/30'
                      : 'bg-game-loss/20 text-game-loss border border-game-loss/30'
                  }`}>
                    {trade.type.toUpperCase()}
                  </span>
                  <span className="font-pixel text-xs text-game-accent">
                    {isBuy ? trade.toToken : trade.fromToken}
                  </span>
                </div>
                <span className="font-mono text-[10px] text-game-muted">
                  {formatTime(trade.timestamp)}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <div className="font-mono text-game-text">
                  <span className="text-game-loss">-{formatAmount(trade.fromAmount, trade.fromToken)}</span>
                  <span className="text-game-muted mx-1">→</span>
                  <span className="text-game-profit">+{formatAmount(trade.toAmount, trade.toToken)}</span>
                </div>
              </div>

              {/* Price at time of trade */}
              <div className="mt-1 font-mono text-[10px] text-game-muted">
                @ ${trade.price < 0.01 ? trade.price.toFixed(6) : trade.price.toFixed(2)} per {isBuy ? trade.toToken : trade.fromToken}
              </div>
            </div>
          )
        })}
      </div>

      {/* Stats Footer */}
      <div className="pt-2 border-t border-game-border flex justify-between text-xs">
        <span className="font-retro text-game-muted">
          {trades.length} trade{trades.length !== 1 ? 's' : ''}
        </span>
        <div className="flex gap-3">
          <span className="font-mono text-game-profit">
            {trades.filter(t => t.type === 'buy').length} buys
          </span>
          <span className="font-mono text-game-loss">
            {trades.filter(t => t.type === 'sell').length} sells
          </span>
        </div>
      </div>
    </div>
  )
}
